import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@lms/ui/dropdown-menu';
import { cn } from '@lms/ui/utils';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslations } from 'use-intl';

// Ported from the Next app's components/multi-select.tsx. Used by the dashboard
// toolbar to filter by location or course.
interface MultiSelectProps<T> {
  options: { value: T; label: string; group?: string }[];
  prefix?: string;
  onSelect?: (selected: T[]) => void;
}

export function MultiSelect<T>({
  options,
  onSelect,
  prefix,
}: MultiSelectProps<T>) {
  const [selected, setSelected] = useState<T[]>(
    options.map(({ value }) => value),
  );
  const t = useTranslations('stats');

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) {
          if (onSelect) onSelect(selected);
        }
      }}
    >
      <DropdownMenuTrigger
        className={cn(
          'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus:ring-ring flex h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
        )}
      >
        <span className="text-left">
          {prefix && (
            <span className="text-muted-foreground">{`${prefix}: `}</span>
          )}
          {selected.length === 0 ? (
            <span className="text-muted-foreground overflow-ellipsis">
              {t('empty_selection')}
            </span>
          ) : (
            <span className="inline overflow-ellipsis">
              {options.length === selected.length
                ? t('all')
                : options
                    .filter(({ value }) => selected.includes(value))
                    .map(({ label }) => label)
                    .join(', ')}
            </span>
          )}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[80dvh] min-w-72 overflow-y-auto">
        <DropdownMenuCheckboxItem
          checked={selected.length === options.length}
          onSelect={(e) => {
            e.preventDefault();
            if (selected.length === options.length) {
              setSelected([]);
            } else {
              setSelected(options.map(({ value }) => value));
            }
          }}
        >
          {t('all')}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {options.map(({ value, label }) => (
          <DropdownMenuCheckboxItem
            checked={selected.includes(value)}
            onSelect={(e) => {
              e.preventDefault();
            }}
            onCheckedChange={(checked) => {
              if (!checked) {
                setSelected((selected) => selected.filter((v) => v !== value));
              } else {
                setSelected((selected) => {
                  const n = selected.filter((v) => v !== value);
                  n.push(value);
                  return n;
                });
              }
            }}
            key={String(value)}
          >
            {label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
